import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { CiBaseball } from "react-icons/ci";
import { connect } from "react-redux";
import { compose } from "redux";

import { updateDepartment, addProcedure, addAttachment, editProcedure, getProcedures, getCompany, addCompanyAvatar } from "../../store/actions/departmentActions";

const CompanyOverlay = ({
    close,
    newlyCreatedCompanyId,
    closeNoSave,
    company_id,
    isLoading,
    edit,
    company,
    getCompanyFN,
    addCompanyAvatarFN,
}) => {
    const colorOptions = useMemo(
        () => ["#2F80ED", "#27AE60", "#9B51E0", "#F2994A", "#EB5757", "#2D9CDB", "#F2C94C", "#E84393"],
        []
    );

    const [form, setForm] = useState({
        title: "",
        description: "",
        color: colorOptions[0],

        risk_appetite: "Moderate",
        risk_acceptance_threshold: 25,

        impact_financial: true,
        impact_operational: true,
        impact_compliance: true,
        impact_reputational: true,

        financial_thresholds: {
            Insignificant: 10,
            Minor: 50,
            Moderate: 250,
            Major: 500,
            Catastrophic: 1000,
        },

        operational_thresholds: {
            Insignificant: 1,
            Minor: 3,
            Moderate: 5,
            Major: 10,
            Catastrophic: 15,
        },

        compliance_thresholds: {
            Insignificant: 10,
            Minor: 50,
            Moderate: 100,
            Major: 250,
            Catastrophic: 500,
        },

        reputational_thresholds: {
            Insignificant: 100,
            Minor: 500,
            Moderate: 1000,
            Major: 5000,
            Catastrophic: 10000,
        },
    });

    useEffect(() => {
        if (company_id) {
            getCompanyFN(company_id)
        }

    }, [company_id]);


    useEffect(() => {
        if (!company) return;

        const impacts = company.impacts || [];

        const mapKey = (impact) =>
            impact === "Catastrophe" ? "Catastrophic" : impact;

        const financial_thresholds = {};
        const operational_thresholds = {};
        const compliance_thresholds = {};
        const reputational_thresholds = {};

        let impact_financial = false;
        let impact_operational = false;
        let impact_compliance = false;
        let impact_reputational = false;

        impacts.forEach((row) => {
            const key = mapKey(row.impact);

            // ---- Financial (convert high → threshold)
            if (row.financial_high !== null) {
                financial_thresholds[key] = row.financial_high / 1000;
                impact_financial = true;
            } else if (row.financial_low !== null) {
                // last row (Catastrophe)
                financial_thresholds[key] = row.financial_low / 1000;
                impact_financial = true;
            }

            // ---- Text fields
            if (row.operational) {
                operational_thresholds[key] = row.operational;
                impact_operational = true;
            }

            if (row.compliance) {
                compliance_thresholds[key] = row.compliance;
                impact_compliance = true;
            }

            if (row.reputational) {
                reputational_thresholds[key] = row.reputational;
                impact_reputational = true;
            }
        });

        setForm({
            title: company.title || "",
            description: company.description || "",
            color: company.color || colorOptions[0],

            risk_appetite: company.risk_appetite || "Moderate",
            risk_acceptance_threshold:
                company.risk_acceptance_threshold || 25,

            impact_financial,
            impact_operational,
            impact_compliance,
            impact_reputational,

            financial_thresholds: {
                Insignificant: financial_thresholds.Insignificant || "",
                Minor: financial_thresholds.Minor || "",
                Moderate: financial_thresholds.Moderate || "",
                Major: financial_thresholds.Major || "",
                Catastrophic: financial_thresholds.Catastrophic || "",
            },

            operational_thresholds: {
                Insignificant: operational_thresholds.Insignificant || "",
                Minor: operational_thresholds.Minor || "",
                Moderate: operational_thresholds.Moderate || "",
                Major: operational_thresholds.Major || "",
                Catastrophic: operational_thresholds.Catastrophic || "",
            },

            compliance_thresholds: {
                Insignificant: compliance_thresholds.Insignificant || "",
                Minor: compliance_thresholds.Minor || "",
                Moderate: compliance_thresholds.Moderate || "",
                Major: compliance_thresholds.Major || "",
                Catastrophic: compliance_thresholds.Catastrophic || "",
            },

            reputational_thresholds: {
                Insignificant: reputational_thresholds.Insignificant || "",
                Minor: reputational_thresholds.Minor || "",
                Moderate: reputational_thresholds.Moderate || "",
                Major: reputational_thresholds.Major || "",
                Catastrophic: reputational_thresholds.Catastrophic || "",
            },
        });
    }, [company]);


    const isEditMode =
        company_id != newlyCreatedCompanyId



    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(company?.icon || null);

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        if (company_id) {
            await addCompanyAvatarFN(company_id, file);
        }
    };

    const [step, setStep] = useState(1);

    const nextStep = () => {
        setStep((prev) => Math.min(prev + 1, 4));
    };

    const prevStep = () => {
        setStep((prev) => Math.max(prev - 1, 1));
    };

    const handleSubmit = () => {
        // final submit on page 4
        //console.log(form)
        close(company_id, form);
    };

    return (
        <div className="w-full">
            <div className="p-6 bg-white rounded-xl shadow-2xl border border-gray-200 max-w-[520px] mx-auto">

                {/* Header */}
                <div className="flex justify-between items-start mb-5">
                    <div>
                        <h1 className="text-[18px] font-semibold text-[#1f1f1f]">
                            {isEditMode ? "Edit Risk Center" : "Create New Risk Center"}
                        </h1>

                        <p className="text-[12px] text-gray-500 mt-1">
                            Step {step} of 4:{" "}
                            {step === 1 && "General Information & Team Members"}
                            {step === 2 && "Risk Appetite"}
                            {step === 3 && "Impact Settings"}
                            {step === 4 && "Acceptance Criteria"}
                        </p>
                    </div>

                    <button
                        className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center"
                        onClick={() => closeNoSave()}
                        title="Close"
                    >
                        <span className="text-gray-500 text-xl leading-none">×</span>
                    </button>
                </div>

                {/* Stepper */}
                <div className="flex items-start justify-between mb-8">
                    {[
                        { id: 1, label: "General" },
                        { id: 2, label: "Appetite" },
                        { id: 3, label: "Impact" },
                        { id: 4, label: "Acceptance" },
                    ].map((item, index, arr) => {
                        const active = step === item.id;
                        const completed = step > item.id;

                        return (
                            <div key={item.id} className="flex items-start flex-1">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`
                    w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold
                    ${active ? "bg-[#070719] text-white ring-4 ring-gray-200" : ""}
                    ${completed ? "bg-[#070719] text-white" : ""}
                    {!active && !completed ? "bg-gray-100 text-gray-500" : ""}
                  `}
                                    >
                                        {item.id}
                                    </div>

                                    <span
                                        className={`
                    text-[10px] mt-2
                    ${active ? "text-[#070719] font-semibold" : "text-gray-500"}
                  `}
                                    >
                                        {item.label}
                                    </span>
                                </div>

                                {index !== arr.length - 1 && (
                                    <div className="h-[2px] bg-gray-200 flex-1 mt-[18px] mx-3" />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Step 1 */}
                {step === 1 && (
                    <div>
                        {/* Avatar upload */}
                        <div className="mb-5 flex items-center gap-4">
                            <div
                                className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-gray-400 transition flex-shrink-0"
                                onClick={() => document.getElementById('avatar-upload').click()}
                            >
                                {avatarPreview
                                    ? <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                                    : <span className="text-2xl text-gray-400">+</span>
                                }
                            </div>
                            <div>
                                <p className="text-[13px] font-semibold text-[#1f1f1f]">Company Avatar</p>
                                <p className="text-[12px] text-gray-500 mt-0.5">Click to upload a logo or image</p>
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('avatar-upload').click()}
                                    className="mt-1.5 text-[12px] text-blue-600 hover:underline"
                                >
                                    {avatarPreview ? 'Change image' : 'Upload image'}
                                </button>
                            </div>
                            <input
                                id="avatar-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleAvatarChange}
                            />
                        </div>

                        <div className="mb-5">
                            <label className="block text-[13px] font-semibold text-[#1f1f1f] mb-2">
                                Risk Center Name *
                            </label>

                            <input
                                type="text"
                                placeholder="Enter risk center name"
                                className="w-full h-11 px-3 bg-gray-100 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
                                value={form.title}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, title: e.target.value }))
                                }
                            />

                            <p className="text-[12px] text-gray-500 mt-2">
                                The name of the organization or entity for risk management
                            </p>
                        </div>

                        <div className="mb-5">
                            <label className="block text-[13px] font-semibold text-[#1f1f1f] mb-2">
                                Risk Center Definition
                            </label>

                            <textarea
                                placeholder="Describe the risk center"
                                rows={5}
                                className="w-full px-3 py-3 bg-gray-100 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm resize-none"
                                value={form.description || ""}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, description: e.target.value }))
                                }
                            />

                            <p className="text-[12px] text-gray-500 mt-2">
                                Provide a detailed description of the risk center's purpose, scope, and strategic objectives
                            </p>
                        </div>

                        {/* Color */}
                        <div className="mb-6">
                            <label className="block text-[13px] font-semibold text-[#1f1f1f] mb-2">
                                Risk Center Color
                            </label>

                            <div className="flex items-center gap-2">
                                {colorOptions.map((c) => {
                                    const active = form.color === c;

                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() =>
                                                setForm((p) => ({ ...p, color: c }))
                                            }
                                            className={`
            w-8 h-8 rounded-full border
            ${active ? "border-black ring-2 ring-gray-300" : "border-gray-200"}
            hover:scale-[1.05] transition
          `}
                                            style={{ backgroundColor: c }}
                                            title={c}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-purple-600 text-lg">✣</span>
              <label className="text-[13px] font-semibold text-[#1f1f1f]">
                Upload Documents for AI Risk Analysis
              </label>
            </div>

            <p className="text-[12px] text-gray-500 mb-3">
              Upload PDF or Word documents to help AI suggest relevant risks for this center
            </p>

            <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center text-center">
              <div className="text-gray-400 text-3xl mb-2">⇧</div>
              <p className="text-[13px] text-gray-600">
                Click to upload or drag and drop
              </p>
              <p className="text-[12px] text-gray-500 mt-1">
                PDF, DOC, DOCX (Max 10MB per file)
              </p>
            </div>
          </div> */}
                    </div>
                )}

                {/* Step 2 */}
                {step === 2 && (
                    <div className="min-h-[320px]">
                        <div className="border border-gray-200 bg-[#F8FBFF] rounded-lg p-4 mb-6">
                            <div className="flex items-center gap-2 mb-2">
                                <i className="far fa-building text-blue-500 text-[14px]" />
                                <h2 className="text-[14px] font-semibold text-[#1f1f1f]">
                                    Define Risk Appetite
                                </h2>
                            </div>

                            <p className="text-[12px] text-gray-500 leading-5">
                                Risk appetite represents your organization's willingness to accept risk in pursuit of objectives.
                            </p>
                        </div>

                        <div className="mb-5">
                            <label className="block text-[13px] font-semibold text-[#1f1f1f] mb-2">
                                Risk Appetite *
                            </label>

                            <select
                                className="w-full h-10 px-3 bg-gray-100 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
                                value={form.risk_appetite || "Moderate"}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, risk_appetite: e.target.value }))
                                }
                            >
                                <option value="Low">Low (Conservative)</option>
                                <option value="Moderate">Moderate (Balanced)</option>
                                <option value="High">High (Aggressive)</option>
                            </select>

                            <p className="text-[12px] text-gray-500 mt-2">
                                Define your organization's overall willingness to take on risk
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 3 */}
                {step === 3 && (
                    <div className="min-h-[320px]">
                        {[
                            {
                                key: "financial_thresholds",
                                enabled: "impact_financial",
                                title: "Financial Impact",
                                desc: "Define financial loss thresholds.",
                                type: "financial",
                            },
                            {
                                key: "operational_thresholds",
                                enabled: "impact_operational",
                                title: "Operational Impact",
                                desc: "Describe operational impact levels.",
                                type: "text",
                            },
                            {
                                key: "compliance_thresholds",
                                enabled: "impact_compliance",
                                title: "Compliance Impact",
                                desc: "Describe compliance impact levels.",
                                type: "text",
                            },
                            {
                                key: "reputational_thresholds",
                                enabled: "impact_reputational",
                                title: "Reputational Impact",
                                desc: "Describe reputational impact levels.",
                                type: "text",
                            },
                        ].map((section) => (
                            <div
                                key={section.key}
                                className="border border-gray-200 rounded-lg p-3 mb-4 bg-white"
                            >
                                <div className="flex items-start gap-2 mb-3">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={!!form[section.enabled]}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                [section.enabled]: e.target.checked,
                                            }))
                                        }
                                    />

                                    <div>
                                        <h3 className="text-[13px] font-semibold text-[#1f1f1f]">
                                            {section.title}
                                        </h3>
                                        <p className="text-[11px] text-gray-500">{section.desc}</p>
                                    </div>
                                </div>

                                {!!form[section.enabled] && (
                                    <div className="bg-gray-50 rounded-md p-3">
                                        {section.type === "financial" ? (
                                            <>
                                                <p className="text-[11px] font-semibold text-gray-600 mb-3">
                                                    Severity Level Thresholds (in K/thousands)
                                                </p>

                                                {[
                                                    ["Insignificant", "<"],
                                                    ["Minor", "<"],
                                                    ["Moderate", "<"],
                                                    ["Major", "<"],
                                                    ["Catastrophic", "≥"],
                                                ].map(([level, sign]) => (
                                                    <div
                                                        key={level}
                                                        className="grid grid-cols-[115px_12px_1fr_16px] items-center gap-2 mb-2"
                                                    >
                                                        <label className="text-[12px] font-semibold text-gray-800">
                                                            {level} ({sign})
                                                        </label>

                                                        <span className="text-[12px] text-gray-500">$</span>

                                                        <input
                                                            type="number"
                                                            className="h-9 px-3 bg-gray-100 border border-transparent rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-300"
                                                            value={form[section.key]?.[level] ?? ""}
                                                            onChange={(e) =>
                                                                setForm((p) => ({
                                                                    ...p,
                                                                    [section.key]: {
                                                                        ...(p[section.key] || {}),
                                                                        [level]: e.target.value,
                                                                    },
                                                                }))
                                                            }
                                                        />

                                                        <span className="text-[12px] text-gray-500">K</span>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-[11px] font-semibold text-gray-600 mb-3">
                                                    Severity Level Descriptions
                                                </p>

                                                {[
                                                    "Insignificant",
                                                    "Minor",
                                                    "Moderate",
                                                    "Major",
                                                    "Catastrophic",
                                                ].map((level) => (
                                                    <div key={level} className="mb-3">
                                                        <label className="block text-[12px] font-semibold text-gray-800 mb-1">
                                                            {level}
                                                        </label>

                                                        <textarea
                                                            rows={2}
                                                            placeholder={`Describe ${level.toLowerCase()} impact`}
                                                            className="w-full px-3 py-2 bg-gray-100 border border-transparent rounded-md text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-gray-300"
                                                            value={form[section.key]?.[level] ?? ""}
                                                            onChange={(e) =>
                                                                setForm((p) => ({
                                                                    ...p,
                                                                    [section.key]: {
                                                                        ...(p[section.key] || {}),
                                                                        [level]: e.target.value,
                                                                    },
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className="border border-blue-200 bg-blue-50 rounded-md p-3">
                            <p className="text-[11px] text-blue-700 leading-5">
                                Note: At least one impact type must be selected. Configure severity
                                thresholds to define what each risk level means.
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 4 */}
                {step === 4 && (
                    <div className="min-h-[320px]">
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <label className="text-[14px] font-semibold text-[#1f1f1f]">
                                    Risk Acceptance Threshold
                                </label>

                                <span className="px-3 py-1 rounded-md bg-gray-100 text-[14px] font-semibold">
                                    {form.risk_acceptance_threshold || 25} / 25
                                </span>
                            </div>

                            <div className="border border-gray-200 rounded-lg p-4">
                                <input
                                    type="range"
                                    min="1"
                                    max="25"
                                    value={form.risk_acceptance_threshold || 25}
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            risk_acceptance_threshold: Number(e.target.value),
                                        }))
                                    }
                                    className="w-full accent-[#070719]"
                                />

                                <div className="flex justify-between text-[12px] text-gray-500 mt-3">
                                    <span>0 (Low Risk)</span>
                                    <span>12-13 (Moderate)</span>
                                    <span>25 (High Risk)</span>
                                </div>
                            </div>

                            <p className="text-[12px] text-gray-500 mt-3 leading-5">
                                Set the maximum acceptable risk score out of 25. Risks above this threshold require immediate attention and mitigation actions.
                            </p>
                        </div>

                        <div className="border border-gray-200 bg-[#F8FBFF] rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <i className="far fa-building text-blue-500 text-[14px]" />
                                <h2 className="text-[14px] font-semibold text-[#1f1f1f]">
                                    Configuration Summary
                                </h2>
                            </div>

                            <div className="space-y-0 text-[13px]">
                                <div className="flex justify-between border-b border-gray-200 py-2">
                                    <span className="text-gray-500">Risk Center Name:</span>
                                    <span className="font-semibold">{form.title || "-"}</span>
                                </div>

                                <div className="flex justify-between border-b border-gray-200 py-2">
                                    <span className="text-gray-500">Risk Appetite:</span>
                                    <span className="px-2 py-0.5 rounded-md bg-white border border-gray-200">
                                        {form.risk_appetite || "Moderate"}
                                    </span>
                                </div>

                                <div className="flex justify-between border-b border-gray-200 py-2">
                                    <span className="text-gray-500">Impact Types Selected:</span>
                                    <span className="font-semibold">
                                        {
                                            [
                                                form.impact_financial,
                                                form.impact_operational,
                                                form.impact_compliance,
                                                form.impact_reputational,
                                            ].filter(Boolean).length
                                        }{" "}
                                        / 4
                                    </span>
                                </div>

                                <div className="flex justify-between border-b border-gray-200 py-2">
                                    <span className="text-gray-500">Acceptance Threshold:</span>
                                    <span className="font-semibold">
                                        {form.risk_acceptance_threshold || 25}/25
                                    </span>
                                </div>

                               
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 pt-4">
                    {step === 1 ? (
                        <button
                            className="h-9 px-5 rounded-md border border-gray-200 text-[13px] hover:bg-gray-50"
                            onClick={() => closeNoSave()}
                        >
                            Cancel
                        </button>
                    ) : (
                        <button
                            className="h-9 px-5 rounded-md border border-gray-200 text-[13px] hover:bg-gray-50"
                            onClick={prevStep}
                        >
                            Back
                        </button>
                    )}

                    {step < 4 ? (
                        <button
                            className="h-9 px-5 rounded-md bg-[#070719] text-white text-[13px] hover:bg-black flex items-center gap-2"
                            onClick={nextStep}
                        >
                            Next
                            <span>›</span>
                        </button>
                    ) : (
                        <button
                            className="h-9 px-5 rounded-md bg-[#070719] text-white text-[13px] hover:bg-black"
                            onClick={handleSubmit}
                        >
                            Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

};

const mapStateToProps = (state) => {
    return {
        company: state.department.company,
        newlyCreatedCompanyId: state.department.newlyCreatedCompanyId,
    }
};

const mapDispatchToProps = (dispatch) => ({
    getCompanyFN: (id) => dispatch(getCompany(id)),
    addCompanyAvatarFN: (company_id, file) => dispatch(addCompanyAvatar(company_id, file)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(CompanyOverlay);
